import * as React from 'react';
import { ChevronDown } from '@volkswagen-onehub/icons-core';
import { styled, css } from '../theme-provider';
import { getCommonProps } from '../helpers/common-props';
import { DisplayTag } from '../display-tag';
import { createA11yOutlineStyles } from '../helpers/a11y';
const ANIMATION_FUNCTION = '0.3s cubic-bezier(0.14, 1.12, 0.67, 0.99)';
// the icon needs to be moved up by the size of the wrapper's bottom padding
const StyledIconWrapper = styled.span.withConfig({ displayName: "StyledIconWrapper", componentId: "sc-jne68a" }) `position:absolute;bottom:${({ theme }) => `${parseInt(theme.size.static150)}px`};display:flex;width:100%;justify-content:flex-end;height:${({ theme }) => theme.size.static350};pointer-events:none;transition:${({ theme }) => `transform ${ANIMATION_FUNCTION}, color ${theme.animation.timingFunction.cubicOut} ${theme.animation.duration.color}`};align-items:center;white-space:nowrap;`;
const StyledLabelText = styled.span.withConfig({ displayName: "StyledLabelText", componentId: "sc-62bnde" }) `font-size:${({ theme }) => theme.fonts.label[100].fontSize};width:100%;display:block;margin:0;padding:0;color:transparent;pointer-events:none;`;
// eslint-disable-next-line @typescript-eslint/no-explicit-any -- StyledLabelText has the right type, this just prevents some TS type inference issue
const StyledLabelTextWithFloating = styled(StyledLabelText).withConfig({ displayName: "StyledLabelTextWithFloating", componentId: "sc-64zw6a" }) `transition:${({ theme }) => `color ${theme.animation.timingFunction.cubicOut} ${theme.animation.duration.color}`};position:absolute;top:0;color:inherit;${({ theme, isFloating, hasValue, labelHeight }) => {
    if (!isFloating || hasValue) {
        return;
    }
    return css `
			font-size: ${theme.fonts.copy[200].fontSize};
			white-space: nowrap;
			text-overflow: ellipsis;
			overflow: hidden;
			padding-right: ${theme.size.static515};
			transition: all 0.25s ease-in-out;
			transform: translateY(${labelHeight}px)
				// Move it down by the padding on the 'StyledLabel'
				translateY(${theme.size.static100})
				// Move it down by the padding on the 'StyledInput'
				translateY(${parseInt(theme.size.static300) / 2}px)
				// Move it down by half the size of the 'StyledInput'
				translateY(-50%);
			// Move it up by half of its own size
		`;
}}`;
const StyledSelect = styled.select.withConfig({ displayName: "StyledSelect", componentId: "sc-1qmh7lk" }) `appearance:none;box-shadow:none;opacity:unset;pointer-events:auto;height:calc(1.15em + ${({ theme }) => theme.size.static150} * 2);width:100%;${({ theme }) => theme.direction === 'rtl'
    ? css `
					padding: ${theme.size.static150} 0 ${theme.size.static150}
						${theme.size.static400};
				`
    : css `
					padding: ${theme.size.static150} ${theme.size.static400}
						${theme.size.static150} 0;
				`} border:0;border-radius:0;border-bottom:${({ theme }) => theme.size.line.default} solid ${({ theme, disabled }) => disabled
    ? theme.colors.controllers.off.disabled
    : theme.colors.border.divider};color:inherit;background:transparent;font-size:${({ theme }) => theme.fonts.copy[200].fontSize};font-family:${({ theme }) => theme.fonts.copy[200].fontFamily};outline:none;cursor:${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};:active,:focus{border-color:${({ theme }) => theme.colors.border.active};~ ${StyledLabelTextWithFloating}{transform:translateY(0);font-size:${({ theme }) => theme.fonts.label[100].fontSize};}~ ${StyledIconWrapper}{transform:${({ disabled }) => disabled ? `rotateX(0)` : `rotateX(180deg)`};}}transition:border-color 0.3s ease;:hover{border-color:${({ theme }) => theme.colors.border.active};}option{color:initial;&:disabled{color:revert;}}:focus-visible{border-radius:calc(${({ theme }) => theme.size.static100} / 2);${({ theme }) => createA11yOutlineStyles(theme, 'solid')};}`;
const StyledLabel = styled.label.withConfig({ displayName: "StyledLabel", componentId: "sc-1wmcxnr" }) `position:relative;display:block;color:${({ disabled, theme }) => theme.colors.content[disabled ? 'disabled' : 'primary']};pointer-events:none;cursor:${({ disabled }) => (disabled ? 'not-allowed' : 'auto')};${({ theme, disabled }) => !disabled &&
    css `
			:hover,
			:focus-within {
				color: ${theme.colors.content.hover};
			}
		`}`;
const StyledErrorText = styled.div.withConfig({ displayName: "StyledErrorText", componentId: "sc-1w6zs1t" }) `display:flex;padding-top:${({ theme }) => theme.size.static100};`;
export const Select = (props) => {
    const ref = React.createRef();
    const labelRef = React.createRef();
    const { appearance, children, disabled, label, defaultValue, value, message, onFocus, onChange, onBlur, required, name, autoComplete, autoFocus, isFloating, } = props;
    const [hasValue, setHasValue] = React.useState(false);
    const [labelHeight, setLabelHeight] = React.useState(0);
    const handleChange = React.useCallback((e) => {
        if (onChange) {
            onChange(e, props);
        }
        setHasValue(true);
    }, [onChange, props]);
    React.useEffect(() => {
        if (ref.current &&
            ref.current.selectedIndex <= 0 &&
            ref.current.querySelector('option:first-child:disabled')) {
            ref.current.selectedIndex = -1;
        }
        else {
            setHasValue(true);
        }
    }, [ref]);
    React.useEffect(() => {
        var _a;
        setLabelHeight(((_a = labelRef.current) === null || _a === void 0 ? void 0 : _a.clientHeight) || 0);
    }, [labelRef]);
    const handleResize = React.useCallback(() => { var _a; return setLabelHeight(((_a = labelRef.current) === null || _a === void 0 ? void 0 : _a.clientHeight) || 0); }, [setLabelHeight, labelRef]);
    React.useEffect(() => {
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, [handleResize]);
    const commonProps = getCommonProps(props);
    const errorMessage = appearance && message ? (React.createElement(StyledErrorText, null,
        React.createElement(DisplayTag, { appearance: appearance, text: message }))) : undefined;
    return (React.createElement(React.Fragment, null,
        React.createElement(StyledLabel, Object.assign({ disabled: disabled }, commonProps),
            label && (React.createElement(StyledLabelText, { "aria-hidden": true, ref: labelRef },
                label,
                required && '*')),
            React.createElement(StyledSelect, { autoComplete: autoComplete, disabled: disabled, required: required, ref: ref || undefined, defaultValue: defaultValue, value: value, onChange: handleChange, onFocus: onFocus, onBlur: onBlur, name: name, autoFocus: autoFocus }, children),
            label && (React.createElement(StyledLabelTextWithFloating, { isFloating: isFloating, hasValue: hasValue, labelHeight: labelHeight },
                label,
                required && '*')),
            React.createElement(StyledIconWrapper, null,
                React.createElement(ChevronDown, { variant: "default", ariaHidden: true }))),
        errorMessage));
};
